import React from 'react';
import { Box, Checkbox, Stack, TextField, Typography } from '@mui/material';

/**
 * Opening hours, one row per weekday.
 *
 * Stored as minutes from midnight and edited as "17:00", because every
 * question the system asks of these is arithmetic and every answer the owner
 * gives is a clock reading. The conversion belongs here, once, rather than in
 * whatever validates the form next.
 *
 * Unticked means closed, rather than 00:00 to 00:00. A closed day and a day
 * open for no minutes are the same to a computer and not at all the same to
 * the person filling this in.
 */

const DAYS = [
  { weekday: 1, label: 'Monday' },
  { weekday: 2, label: 'Tuesday' },
  { weekday: 3, label: 'Wednesday' },
  { weekday: 4, label: 'Thursday' },
  { weekday: 5, label: 'Friday' },
  { weekday: 6, label: 'Saturday' },
  { weekday: 0, label: 'Sunday' },
];

const toClock = (minutes) => {
  const m = Math.max(0, Math.min(1440, Number(minutes) || 0));
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

const toMinutes = (clock) => {
  const [h, m] = String(clock || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

export default function OpeningHoursField({ value = [], onChange }) {
  const rowFor = (weekday) => value.find((r) => r.weekday === weekday) || null;

  const setDay = (weekday, next) => {
    const without = value.filter((r) => r.weekday !== weekday);
    onChange(next ? [...without, next].sort((a, b) => a.weekday - b.weekday) : without);
  };

  return (
    <Box>
      <Typography variant='caption' color='text.secondary' display='block' mb='8px'>
        Leave a day unticked and nothing can be booked on it.
      </Typography>
      <Stack spacing='6px'>
        {DAYS.map(({ weekday, label }) => {
          const row = rowFor(weekday);
          const open = Boolean(row);
          return (
            /**
             * Wraps, and the clocks share what is left.
             *
             * Measured: a tick, a 96px day name, two 120px time fields and the
             * word "to" need 419px, inside a dialog that is 334px wide on a
             * 390px phone. Fixed widths that fit on a laptop are how a form
             * ends up 85px off the side of the screen with no way to reach the
             * closing time.
             */
            <Stack
              key={weekday}
              direction='row'
              alignItems='center'
              gap='10px'
              flexWrap='wrap'
              useFlexGap
            >
              <Checkbox
                size='small'
                checked={open}
                onChange={(e) => setDay(weekday, e.target.checked
                  ? { weekday, opens: 9 * 60, closes: 17 * 60 }
                  : null)}
                inputProps={{ 'aria-label': label }}
              />
              <Typography variant='body2' sx={{ width: { xs: 'auto', sm: 96 }, flexShrink: 0 }}>
                {label}
              </Typography>
              <TextField
                size='small' type='time' disabled={!open}
                value={toClock(row?.opens ?? 9 * 60)}
                onChange={(e) => {
                  const opens = toMinutes(e.target.value);
                  if (opens == null) return;
                  setDay(weekday, { ...row, weekday, opens });
                }}
                sx={{ width: { xs: 'auto', sm: 120 }, flex: { xs: 1, sm: '0 0 auto' }, minWidth: 110 }}
              />
              <Typography variant='caption' color='text.secondary'>to</Typography>
              <TextField
                size='small' type='time' disabled={!open}
                value={toClock(row?.closes ?? 17 * 60)}
                onChange={(e) => {
                  const closes = toMinutes(e.target.value);
                  if (closes == null) return;
                  setDay(weekday, { ...row, weekday, closes });
                }}
                sx={{ width: { xs: 'auto', sm: 120 }, flex: { xs: 1, sm: '0 0 auto' }, minWidth: 110 }}
              />
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
}
